import React, { useEffect, useMemo, useState, useRef } from "react";
import api from "../lib/api";
import { Plus, ChevronLeft, ChevronRight, Copy, Trash2, Edit, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  utcIsoToLocalInput, localInputToUtcIso,
  utcIsoToRomeYmd, utcIsoToRomeHM,
  fmtRomeDate, fmtRomeTime, fmtRomeDateTime,
} from "../lib/tz";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const DAYS_SHORT = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const CATEGORY_LABELS = {
  appuntamento_cliente:"Appuntamento cliente", scadenza_pratica:"Scadenza pratica",
  scadenza_bando:"Scadenza bando", richiesta_documenti:"Richiesta documenti",
  richiamo:"Richiamo", riunione:"Riunione", attivita_interna:"Attività interna",
  pagamento:"Pagamento", altro:"Altro",
};
const CATEGORY_COLORS = {
  appuntamento_cliente:"#B40000", scadenza_pratica:"#F59E0B", scadenza_bando:"#8B0000",
  richiesta_documenti:"#0D6EFD", richiamo:"#6F42C1", riunione:"#20B486",
  attivita_interna:"#607D8B", pagamento:"#2E7D32", altro:"#9E9E9E",
};

function pad(n){ return String(n).padStart(2,"0"); }
function ymd(d){ return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
function startOfWeek(d){ const x = new Date(d); const wd = (x.getDay()+6)%7; x.setDate(x.getDate()-wd); x.setHours(0,0,0,0); return x; }
function addDays(d, n){ const x = new Date(d); x.setDate(x.getDate()+n); return x; }

// Ora corrente Europe/Rome formattata come "YYYY-MM-DDTHH:MM" (per default sul form)
function nowRomeLocalInput() {
  return utcIsoToLocalInput(new Date().toISOString()).slice(0, 13) + ":00";
}

export default function CalendarPage() {
  const [view, setView] = useState("month");  // month|week|day|list
  const [anchor, setAnchor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [modal, setModal] = useState(null); // { mode:'create'|'edit', data:{} }
  const { has } = useAuth();

  const range = useMemo(() => {
    if (view === "month") {
      const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const e = new Date(anchor.getFullYear(), anchor.getMonth()+1, 0);
      return [addDays(s, -7), addDays(e, 7)];
    }
    if (view === "week") {
      const s = startOfWeek(anchor);
      return [s, addDays(s, 6)];
    }
    if (view === "day") return [anchor, anchor];
    // list
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    return [s, addDays(s, 60)];
  }, [view, anchor]);

  const load = async () => {
    const params = { start: ymd(range[0]), end: ymd(range[1]) };
    if (filterCat) params.category = filterCat;
    const r = await api.get("/calendar", { params });
    setEvents(r.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range[0].getTime(), range[1].getTime(), filterCat]);

  const eventsByDay = useMemo(() => {
    const map = {};
    (events || []).forEach(e => {
      // Raggruppa per giorno civile in Europe/Rome (evita off-by-one attorno alla mezzanotte)
      const key = utcIsoToRomeYmd(e.start_at);
      (map[key] = map[key] || []).push(e);
    });
    return map;
  }, [events]);

  const openCreate = (prefill={}) => setModal({ mode:"create", data:{
    title:"", category:"appuntamento_cliente", event_type:"appointment",
    // start_at è un valore per input datetime-local espresso in ora Rome
    start_at: nowRomeLocalInput(),
    end_at:"", all_day:false, priority:"media", is_shared:false,
    subject_id:"", practice_id:"", partner_id:"", bando_id:"",
    location:"", meeting_url:"", notes:"", reminders:[15],
    recurrence_rule: null, ...prefill,
  }});
  const openEdit = (ev) => {
    const d = { ...ev,
      // Converte UTC → ora locale Rome per gli input datetime-local
      start_at: utcIsoToLocalInput(ev.start_at),
      end_at: utcIsoToLocalInput(ev.end_at),
      subject_id: ev.subject_id||"", practice_id: ev.practice_id||"",
      partner_id: ev.partner_id||"", bando_id: ev.bando_id||"",
    };
    setModal({ mode:"edit", data: d });
  };

  // ---------- Drag & drop (spostamento evento tra giorni, solo mese/settimana) ----------
  const dragRef = useRef(null);
  const onDragStart = (ev, e) => { dragRef.current = ev; e.dataTransfer.setData("text/plain", ev.id); };
  const onDropOnDate = async (targetDateStr, e) => {
    e.preventDefault();
    const ev = dragRef.current;
    if (!ev) return;
    dragRef.current = null;
    // Preserva ORA/MINUTI Rome dell'evento originale
    const { hour, minute } = utcIsoToRomeHM(ev.start_at);
    const newStartLocal = `${targetDateStr}T${hour}:${minute}`;
    const newStartUtc = localInputToUtcIso(newStartLocal);
    if (!newStartUtc) return;
    // Mantieni la durata
    let newEndUtc = null;
    if (ev.end_at) {
      const durMs = new Date(ev.end_at).getTime() - new Date(ev.start_at).getTime();
      newEndUtc = new Date(new Date(newStartUtc).getTime() + durMs).toISOString();
    }
    const isOccurrence = ev.is_occurrence;
    const id = isOccurrence ? ev.parent_series_id : ev.id;
    try {
      await api.patch(`/calendar/${id}`, {
        start_at: newStartUtc,
        end_at: newEndUtc,
        edit_scope: "single",
      });
      toast.success("Evento spostato");
      load();
    } catch {}
  };

  const nav = (dir) => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7*dir);
    else if (view === "day") d.setDate(d.getDate() + dir);
    else d.setMonth(d.getMonth() + dir);
    setAnchor(d);
  };

  const monthTitle = `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
  const today = new Date();

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <h1 className="ma-h1" data-testid="calendar-title">Agenda</h1>
        <div className="ma-actions" style={{gap:6, flexWrap:"wrap"}}>
          <div className="ma-tabs" style={{border:"none", marginBottom:0}}>
            {[["month","Mese"],["week","Settimana"],["day","Giorno"],["list","Elenco"]].map(([k,l])=>(
              <button key={k} className={`ma-tab ${view===k?"active":""}`} onClick={()=>setView(k)} data-testid={`cal-view-${k}`}>{l}</button>
            ))}
          </div>
          <select className="ma-input-sm" value={filterCat} onChange={e=>setFilterCat(e.target.value)}>
            <option value="">Tutte le categorie</option>
            {Object.entries(CATEGORY_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button className="ma-btn-secondary" onClick={()=>nav(-1)}><ChevronLeft size={14}/></button>
          <button className="ma-btn-secondary" onClick={()=>setAnchor(new Date())}>Oggi</button>
          <div className="ma-cal-month">{view==="day" ? anchor.toLocaleDateString("it-IT", {weekday:"long", day:"numeric", month:"long", year:"numeric"}) : monthTitle}</div>
          <button className="ma-btn-secondary" onClick={()=>nav(1)}><ChevronRight size={14}/></button>
          {has("calendar.write") && (
            <button className="ma-btn-primary-sm" onClick={()=>openCreate()} data-testid="btn-new-event"><Plus size={14}/> Nuovo evento</button>
          )}
        </div>
      </div>

      {view === "month" && (
        <MonthView anchor={anchor} eventsByDay={eventsByDay} today={today}
                   onOpen={openEdit} onCreate={(d)=>openCreate({start_at: d+"T09:00"})}
                   onDragStart={onDragStart} onDrop={onDropOnDate}/>
      )}
      {view === "week" && (
        <WeekView anchor={anchor} eventsByDay={eventsByDay} today={today}
                   onOpen={openEdit} onCreate={(d)=>openCreate({start_at: d+"T09:00"})}
                   onDragStart={onDragStart} onDrop={onDropOnDate}/>
      )}
      {view === "day" && (
        <DayView day={anchor} events={eventsByDay[ymd(anchor)]||[]} onOpen={openEdit} onCreate={(d)=>openCreate({start_at: d+"T09:00"})}/>
      )}
      {view === "list" && (
        <ListView events={events} onOpen={openEdit}/>
      )}

      {modal && <EventModal mode={modal.mode} data={modal.data} onClose={()=>setModal(null)} onDone={()=>{ setModal(null); load(); }}/>}
    </div>
  );
}

function EventPill({ ev, onOpen, onDragStart }) {
  const color = ev.color || CATEGORY_COLORS[ev.category] || "#B40000";
  return (
    <div className="ma-cal-event" style={{background:color, cursor:"pointer"}}
         draggable onDragStart={(e)=>onDragStart(ev, e)}
         onClick={(e)=>{e.stopPropagation(); onOpen(ev);}}
         title={ev.title}>
      <span className="ma-cal-time">{fmtRomeTime(ev.start_at)}</span>
      <span className="ma-cal-title">{ev.title}{ev.parent_series_id?" ↻":""}</span>
    </div>
  );
}

function MonthView({ anchor, eventsByDay, today, onOpen, onCreate, onDragStart, onDrop }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth()+1, 0);
  const startWeekday = (first.getDay()+6)%7;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(d);
  const isToday = (d) => d && today.getFullYear()===anchor.getFullYear() && today.getMonth()===anchor.getMonth() && today.getDate()===d;

  return (
    <div className="ma-cal-grid">
      {DAYS_SHORT.map(d => <div key={d} className="ma-cal-dayname">{d}</div>)}
      {cells.map((d,i) => {
        const key = d ? ymd(new Date(anchor.getFullYear(), anchor.getMonth(), d)) : `empty-${i}`;
        const evs = d ? (eventsByDay[key]||[]) : [];
        return (
          <div key={key} className={`ma-cal-cell ${!d?"empty":""} ${isToday(d)?"today":""}`}
                onDragOver={d?(e)=>e.preventDefault():undefined}
                onDrop={d?(e)=>onDrop(key,e):undefined}
                onDoubleClick={d?()=>onCreate(key):undefined}
                style={{cursor:d?"pointer":"default"}}>
            {d && <div className="ma-cal-daynum">{d}</div>}
            {evs.slice(0,4).map(e => <EventPill key={e.id + (e.series_original_start||"")} ev={e} onOpen={onOpen} onDragStart={onDragStart}/>)}
            {evs.length > 4 && <div className="ma-sub-xs">+{evs.length-4} altri</div>}
          </div>
        );
      })}
    </div>
  );
}

function WeekView({ anchor, eventsByDay, today, onOpen, onCreate, onDragStart, onDrop }) {
  const s = startOfWeek(anchor);
  const days = Array.from({length:7}, (_,i) => addDays(s, i));
  return (
    <div className="ma-cal-grid" style={{gridTemplateColumns:"repeat(7,1fr)"}}>
      {days.map(d => <div key={"h"+d.getTime()} className="ma-cal-dayname">
        {DAYS_SHORT[(d.getDay()+6)%7]} {d.getDate()}/{d.getMonth()+1}
      </div>)}
      {days.map(d => {
        const key = ymd(d);
        const evs = eventsByDay[key]||[];
        const isTd = d.toDateString() === today.toDateString();
        return (
          <div key={key} className={`ma-cal-cell ${isTd?"today":""}`}
               style={{minHeight:280, cursor:"pointer"}}
               onDragOver={(e)=>e.preventDefault()}
               onDrop={(e)=>onDrop(key,e)}
               onDoubleClick={()=>onCreate(key)}>
            {evs.map(e => <EventPill key={e.id+(e.series_original_start||"")} ev={e} onOpen={onOpen} onDragStart={onDragStart}/>)}
            {evs.length===0 && <div className="ma-sub-xs" style={{opacity:0.5}}>—</div>}
          </div>
        );
      })}
    </div>
  );
}

function DayView({ day, events, onOpen, onCreate }) {
  const hours = Array.from({length:24}, (_,h) => h);
  const key = ymd(day);
  return (
    <div className="ma-info-block" onDoubleClick={()=>onCreate(key)}>
      <h3 className="ma-h3">Eventi del {day.toLocaleDateString("it-IT")}</h3>
      {events.length === 0 && <div className="ma-empty">Nessun evento. Doppio-click qui per crearne uno.</div>}
      {hours.map(h => {
        // Filtra per ora Europe/Rome
        const hEvents = events.filter(e => parseInt(utcIsoToRomeHM(e.start_at).hour, 10) === h);
        if (hEvents.length === 0) return null;
        return (
          <div key={h} style={{display:"flex", gap:12, borderBottom:"1px solid var(--ma-border)", padding:"8px 0"}}>
            <div style={{width:60, fontWeight:600, color:"var(--ma-text-muted)"}}>{pad(h)}:00</div>
            <div style={{flex:1, display:"flex", flexDirection:"column", gap:4}}>
              {hEvents.map(e => (
                <div key={e.id+(e.series_original_start||"")} onClick={()=>onOpen(e)} style={{cursor:"pointer", padding:"6px 10px", background:e.color||CATEGORY_COLORS[e.category], color:"#fff", borderRadius:6}}>
                  <b>{fmtRomeTime(e.start_at)}</b> — {e.title}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ListView({ events, onOpen }) {
  const sorted = [...(events||[])].sort((a,b) => new Date(a.start_at) - new Date(b.start_at));
  return (
    <div className="ma-table-wrap"><table className="ma-table">
      <thead><tr><th>Data</th><th>Ora</th><th>Titolo</th><th>Categoria</th><th>Luogo</th></tr></thead>
      <tbody>
        {sorted.map(e => (
          <tr key={e.id+(e.series_original_start||"")} onClick={()=>onOpen(e)} style={{cursor:"pointer"}}>
            <td>{fmtRomeDate(e.start_at)}</td>
            <td>{fmtRomeTime(e.start_at)}</td>
            <td className="ma-link">{e.title}{e.parent_series_id?" ↻":""}</td>
            <td><span className="ma-pill">{CATEGORY_LABELS[e.category]||e.category}</span></td>
            <td>{e.location||"—"}</td>
          </tr>
        ))}
        {sorted.length===0 && <tr><td colSpan={5} className="ma-empty">Nessun evento nel periodo.</td></tr>}
      </tbody>
    </table></div>
  );
}

function EventModal({ mode, data, onClose, onDone }) {
  const [f, setF] = useState(data);
  const [subjects, setSubjects] = useState([]);
  const [practices, setPractices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [bandi, setBandi] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editScope, setEditScope] = useState("single");
  const [recEnabled, setRecEnabled] = useState(!!data.recurrence_rule);
  const [rec, setRec] = useState(data.recurrence_rule || { freq:"weekly", interval:1, count:10 });

  useEffect(() => {
    api.get("/subjects", { params: { limit: 200 } })
      .then(r => setSubjects(r.data.items || []))
      .catch(() => setSubjects([]));
    api.get("/practices", { params: { limit: 200 } })
      .then(r => setPractices(r.data.items || []))
      .catch(() => setPractices([]));
    api.get("/partners", { params: { limit: 200 } })
      .then(r => setPartners(r.data.items || []))
      .catch(() => setPartners([]));
    api.get("/bandi", { params: { limit: 200 } })
      .then(r => setBandi(r.data.items || []))
      .catch(() => setBandi([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const p = { ...f };
      // start_at/end_at nel form sono in ora locale Rome ("YYYY-MM-DDTHH:MM") -> converti in UTC ISO
      p.start_at = localInputToUtcIso(f.start_at);
      p.end_at = f.end_at ? localInputToUtcIso(f.end_at) : null;
      ["subject_id","practice_id","partner_id","bando_id","responsible_user_id"].forEach(k => { if (!p[k]) p[k] = null; });
      // Ricorrenza: until è un date (YYYY-MM-DD) → converti a fine giornata Rome
      let rr = recEnabled ? { ...rec } : null;
      if (rr && rr.until) {
        // rec.until è memorizzato come ISO in stato; se è solo YYYY-MM-DD, tratta come fine giornata Rome
        const untilStr = String(rr.until);
        if (/^\d{4}-\d{2}-\d{2}$/.test(untilStr)) {
          rr.until = localInputToUtcIso(untilStr + "T23:59");
        }
      }
      p.recurrence_rule = rr;
      if (mode === "create") {
        await api.post("/calendar", p);
        toast.success("Evento creato");
      } else {
        p.edit_scope = editScope;
        const targetId = f.parent_series_id || f.id;
        await api.patch(`/calendar/${targetId}`, p);
        toast.success("Evento aggiornato");
      }
      onDone();
    } catch { setBusy(false); }
  };

  const doDelete = async (scope) => {
    if (!window.confirm("Confermi eliminazione?")) return;
    const targetId = f.parent_series_id || f.id;
    await api.delete(`/calendar/${targetId}?scope=${scope}`);
    toast.success("Evento archiviato");
    onDone();
  };
  const doDuplicate = async () => {
    const targetId = f.parent_series_id || f.id;
    await api.post(`/calendar/${targetId}/duplicate`);
    toast.success("Duplicato");
    onDone();
  };

  const isRecurring = !!f.parent_series_id || recEnabled;

  return (
    <div className="ma-modal-overlay" onClick={onClose}>
      <div className="ma-modal ma-modal-rel" onClick={e=>e.stopPropagation()}>
        <div className="ma-modal-head">{mode==="create" ? "Nuovo evento" : "Modifica evento"}</div>
        <form onSubmit={submit} className="ma-form-panel">
          <div className="ma-form-grid">
            <div className="ma-col-full"><label className="ma-label">Titolo *</label>
              <input className="ma-input" required value={f.title} onChange={e=>setF({...f, title:e.target.value})} data-testid="event-title"/></div>
            <div><label className="ma-label">Categoria</label>
              <select className="ma-input" value={f.category} onChange={e=>setF({...f, category:e.target.value})} data-testid="event-category">
                {Object.entries(CATEGORY_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
            <div><label className="ma-label">Priorità</label>
              <select className="ma-input" value={f.priority} onChange={e=>setF({...f, priority:e.target.value})}>
                <option value="bassa">Bassa</option><option value="media">Media</option><option value="alta">Alta</option><option value="urgente">Urgente</option>
              </select></div>
            <div><label className="ma-label">Inizio *</label>
              <input type="datetime-local" className="ma-input" required value={f.start_at} onChange={e=>setF({...f, start_at:e.target.value})} data-testid="event-start"/></div>
            <div><label className="ma-label">Fine</label>
              <input type="datetime-local" className="ma-input" value={f.end_at} onChange={e=>setF({...f, end_at:e.target.value})}/></div>
            <div className="ma-col-full">
              <label className="ma-checkbox"><input type="checkbox" checked={f.all_day} onChange={e=>setF({...f, all_day:e.target.checked})}/> Evento tutto il giorno</label>
              <label className="ma-checkbox" style={{marginLeft:20}}><input type="checkbox" checked={f.is_shared} onChange={e=>setF({...f, is_shared:e.target.checked})}/> Calendario condiviso</label>
            </div>
            <div><label className="ma-label">Cliente</label>
              <select className="ma-input" value={f.subject_id||""} onChange={e=>setF({...f, subject_id:e.target.value})}>
                <option value="">—</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.display_name}</option>)}
              </select></div>
            <div><label className="ma-label">Pratica</label>
              <select className="ma-input" value={f.practice_id||""} onChange={e=>setF({...f, practice_id:e.target.value})}>
                <option value="">—</option>
                {practices.map(p => <option key={p.id} value={p.id}>{p.code} · {p.title}</option>)}
              </select></div>
            <div><label className="ma-label">Partner</label>
              <select className="ma-input" value={f.partner_id||""} onChange={e=>setF({...f, partner_id:e.target.value})}>
                <option value="">—</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            <div><label className="ma-label">Bando/Strumento</label>
              <select className="ma-input" value={f.bando_id||""} onChange={e=>setF({...f, bando_id:e.target.value})}>
                <option value="">—</option>
                {bandi.map(b => <option key={b.id} value={b.id}>{b.code} · {b.title}</option>)}
              </select></div>
            <div className="ma-col-2"><label className="ma-label">Luogo</label>
              <input className="ma-input" value={f.location||""} onChange={e=>setF({...f, location:e.target.value})}/></div>
            <div className="ma-col-2"><label className="ma-label">Link riunione</label>
              <input className="ma-input" value={f.meeting_url||""} onChange={e=>setF({...f, meeting_url:e.target.value})}/></div>
            <div className="ma-col-full"><label className="ma-label">Note</label>
              <textarea className="ma-input" rows={2} value={f.notes||""} onChange={e=>setF({...f, notes:e.target.value})}/></div>
            <div className="ma-col-full">
              <label className="ma-label">Promemoria (minuti prima, separati da virgola)</label>
              <input className="ma-input" placeholder="es. 15, 60, 1440" value={(f.reminders||[]).join(", ")}
                     onChange={e=>setF({...f, reminders: e.target.value.split(",").map(x=>parseInt(x.trim())).filter(x=>!isNaN(x)&&x>=0)})}/>
            </div>
            <div className="ma-col-full">
              <label className="ma-checkbox"><input type="checkbox" checked={recEnabled} onChange={e=>setRecEnabled(e.target.checked)} data-testid="event-rec-toggle"/> Ricorrenza</label>
            </div>
            {recEnabled && (
              <>
                <div><label className="ma-label">Frequenza</label>
                  <select className="ma-input" value={rec.freq} onChange={e=>setRec({...rec, freq:e.target.value})}>
                    <option value="daily">Giornaliera</option><option value="weekly">Settimanale</option>
                    <option value="monthly">Mensile</option><option value="yearly">Annuale</option>
                  </select></div>
                <div><label className="ma-label">Ogni (intervallo)</label>
                  <input type="number" min="1" className="ma-input" value={rec.interval||1} onChange={e=>setRec({...rec, interval:parseInt(e.target.value)||1})}/></div>
                <div><label className="ma-label">Numero occorrenze</label>
                  <input type="number" min="1" className="ma-input" value={rec.count||""} onChange={e=>setRec({...rec, count: e.target.value?parseInt(e.target.value):null, until:null})}/></div>
                <div><label className="ma-label">Fine ricorrenza</label>
                  <input type="date" className="ma-input" value={rec.until? String(rec.until).slice(0,10):""} onChange={e=>setRec({...rec, until:e.target.value?new Date(e.target.value).toISOString():null, count:null})}/></div>
              </>
            )}
            {mode==="edit" && isRecurring && (
              <div className="ma-col-full">
                <label className="ma-label">Applicare la modifica a</label>
                <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
                  {[["single","Solo questa occorrenza"],["following","Questa e le successive"],["series","Tutta la serie"]].map(([k,l]) => (
                    <label key={k} className="ma-checkbox">
                      <input type="radio" name="scope" checked={editScope===k} onChange={()=>setEditScope(k)}/> {l}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="ma-form-actions" style={{justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:6}}>
              {mode==="edit" && (
                <>
                  <button type="button" className="ma-btn-secondary" onClick={doDuplicate}><Copy size={14}/> Duplica</button>
                  {isRecurring
                    ? (<>
                        <button type="button" className="ma-btn-danger-sm" onClick={()=>doDelete("single")}><Trash2 size={14}/> Elimina occorrenza</button>
                        <button type="button" className="ma-btn-danger-sm" onClick={()=>doDelete("series")}><Trash2 size={14}/> Elimina serie</button>
                      </>)
                    : <button type="button" className="ma-btn-danger-sm" onClick={()=>doDelete("single")}><Trash2 size={14}/> Archivia</button>
                  }
                </>
              )}
            </div>
            <div style={{display:"flex", gap:6}}>
              <button type="button" className="ma-btn-secondary" onClick={onClose}>Annulla</button>
              <button type="submit" className="ma-btn-primary" disabled={busy} data-testid="event-submit">{busy?"Salvataggio…":(mode==="create"?"Crea evento":"Salva modifiche")}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
